/**
 * Feedback-tab TanStack Query hook (Plan 04-07 Task 1).
 *
 * Single hook driving the paginated `/salescoach/api/feedback/cards` endpoint. Query
 * key includes `filtersQuestion(filters)` + every feedback-only param so cache
 * entries are keyed by semantic shape (not by raw URL). `staleTime: 60_000`
 * + `placeholderData: keepPreviousData` match the Quality / Engagement tabs.
 * Since the recount rule the placeholder payload no longer reaches the page
 * while a request is in flight (hooks/use-filtered-query.ts) — but it DOES
 * while this query is disabled, which is why the page's accumulator still
 * guards on `isPlaceholderData`. PAGING keeps its rows, because the page
 * accumulates them itself; a SORT change does not — it is part of the
 * signature, so the list blanks like any other change of question, not because
 * the next page
 * is in flight.
 */

import { keepPreviousData } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import {
  resolveRange,
  filtersQuestion,
  type Filters,
} from '@/lib/filters-schema';
import {
  fetchFeedbackCards,
  type FetchFeedbackCardsParams,
} from '@/services/feedback-api';
import type { FeedbackCardsResponse } from '@/services/api-types';

const STALE_TIME = 60_000;

/**
 * P1-1 (v1.4-dashboard-ssot): detect an inverted rating range at the hook
 * layer and disable the query so the fetch does NOT fire with invalid params.
 * Previously `minRating=5 & maxRating=1` produced `400` from the backend Zod
 * schema, which surfaced as a generic "Feedback cards failed to load / Retry"
 * card and a retry loop. The page-level code inspects the returned
 * `invalidRange` flag to show an inline hint instead of the error card.
 */
export function isInvalidRatingRange(
  minRating: number | undefined,
  maxRating: number | undefined,
): boolean {
  if (minRating === undefined || maxRating === undefined) return false;
  if (!Number.isFinite(minRating) || !Number.isFinite(maxRating)) return false;
  return minRating > maxRating;
}

/**
 * Fetch paginated feedback cards with the given filters + sort / pagination
 * knobs. Returns the unwrapped `FeedbackCardsResponse` (`{rows, total}`).
 *
 * The query key captures every param that affects the server response so
 * tweaking the sort dropdown re-issues a fetch; a stable array for the key
 * means repeated renders with the same values hit the cache.
 */
export function useFeedbackCards(
  filters: Filters,
  params: FetchFeedbackCardsParams = {},
) {
  // P1-1: gate the fetch when min > max. The caller still gets a result
  // object (state is `idle` / no data); we surface the reason via the
  // sibling helper so FeedbackPage can render an inline hint instead of
  // the generic ErrorRetry card.
  const invalidRange = isInvalidRatingRange(params.minRating, params.maxRating);
  // Resolved [from, to] joins the query key so a preset like '7d' reliably
  // invalidates when the day rolls over. `stripDefaults` otherwise drops
  // from/to for non-custom presets, leaving today's key identical to
  // yesterday's — the user saw stale feedback after midnight UTC. Matches
  // the pattern applied to use-quality / use-engagement.
  const range = resolveRange(filters);
  /* Every one of `sort`, `limit` and `offset` is in this key and blanks it:
     the endpoint returns a PAGE, so a re-sort brings back the first N rows in
     the new order — a different SET of traces, not the same ones rearranged
     (owner's ruling 3, 2026-08-20). */
  return useFilteredQuery<FeedbackCardsResponse>({
    queryKey: [
    'v2',
    'feedback',
    'cards',
    params.offset ?? null,
    params.minRating ?? null,
    params.maxRating ?? null,
    params.hasText ?? null,
    params.userId ?? null,
    // K2 — partitioning by feedback source changes the server response, so
    // the cache key has to include it.
    params.source ?? null,
    range.from,
    range.to,
    params.sort ?? null,
    params.limit ?? null,
    filtersQuestion(filters),
  ],
    queryFn: async () => (await fetchFeedbackCards(filters, params)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    // P1-1: don't fire the query when the rating range is inverted. The
    // placeholder payload stays visible to THIS hook's caller — a disabled
    // query fetches nothing, so `useFilteredQuery` leaves it alone — and the
    // page decides what to do with it, alongside the inline hint it renders
    // from `invalidRange` (sibling helper `isInvalidRatingRange`).
    enabled: !invalidRange,
  });
}
